import "./PurchaseOrder.css";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../../Services/toaster";
import editIcon from "../../../assets/icons/edit.png";
import deleteIcon from "../../../assets/icons/delete.png";
import BackIcon from "../../../assets/icons/arrow.png";
import POApprovals from "./POApprovals";
import { useState, useEffect, useCallback, useRef } from "react";
// ─────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];
const CURRENCY_OPTIONS = ["INR", "USD", "EUR", "GBP"];
const PAYMENT_TERMS = [
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Due on Receipt",
];

const EMPTY_LINE = {
  line_no: 0,
  item_category: "",
  item_code: "",
  item_group: "",
  description: "",
  qty: 0,
  consumed_qty: 0,
  unit: "",
  price: 0,
  price_fc: 0,
  discount_percent: 0,
  discount_amount: 0,
  discount_amount_fc: 0,
  tax_code: "",
  tax_rate: 0,
  line_total: 0,
  line_total_fc: 0,
  tax_amount: 0,
  tax_amount_fc: 0,
  total_amount: 0,
  total_amount_fc: 0,
  remarks: "",
};

const EMPTY_FORM = {
  event_id: "",
  vendor_code: "",
  vendor_name: "",
  po_number: "",
  po_date: "",
  required_date: "",
  delivery_date: "",
  priority: "Medium",
  currency: "INR",
  currency_rate: 1,
  department: "",
  contact_person: "",
  email: "",
  phone: "",
  vendor_reference_no: "",
  vendor_address: "",
  payment_term: "Net 30",
  subtotal: 0,
  subtotal_fc: 0,
  vat_sum: 0,
  vat_sum_fc: 0,
  discount: 0,
  discount_fc: 0,
  doc_total: 0,
  doc_total_fc: 0,
  status: "Draft",
  remarks: "",
  lines: [],
  approvals: [],
};

// ─────────────────────────────────────────────────────────
//  PURCHASE ORDERS VIEW
// ─────────────────────────────────────────────────────────
function PurchaseOrdersView() {
  // ─── State ──────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const submitLockRef = useRef(false);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // ─── Dropdown Data ──────────────────────────────────────
  const [vendors, setVendors] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── API Calls ──────────────────────────────────────────

  const fetchPOs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/po/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      const data = response.data?.data || response.data || [];
      setPos(data);
    } catch (err) {
      console.error("Failed to load POs:", err.response?.data);
      toastError("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  const fetchPOById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/api/po/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        return response.data?.data || response.data;
      } catch (err) {
        console.error("Failed to fetch PO:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── Fetch Approval Document for PO ────────────────────
  const fetchApprovalDocument = useCallback(async () => {
    try {
      const documentCode = "PO";

      const response = await axios.get(
        `${API_URL}/approval/document/${documentCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );

      const approvalData = response.data?.data || [];

      const approvals = approvalData.map((item) => ({
        approval_level: item.approval_level,
        approver_user_id: item.approver_user_id,
        status: "Pending",
        remarks: "",
      }));

      return approvals;
    } catch (err) {
      console.error("Failed to fetch approval document:", err.response?.data);
      return [];
    }
  }, [API_URL, token]);

  const createPO = async (poData) => {
    try {
      const response = await axios.post(`${API_URL}/api/po/create`, poData, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (err) {
      console.error("Create PO error:", err.response?.data);
      throw err;
    }
  };

  const updatePO = async (id, poData) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/po/${id}/update`,
        poData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update PO error:", err.response?.data);
      throw err;
    }
  };

  const deletePO = async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/api/po/${id}/delete`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      return response.data;
    } catch (err) {
      console.error("Delete PO error:", err.response?.data);
      throw err;
    }
  };

  // ─── Fetch Dropdown Data ──────────────────────────────

  const fetchDropdownData = useCallback(async () => {
    if (dropdownsLoaded) return;

    setLoadingDropdowns(true);
    try {
      const [vendorsRes, eventsRes, categoriesRes, unitsRes, taxCodesRes] =
        await Promise.all([
          axios.get(`${API_URL}/vendor/list/all`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }),
          axios.get(`${API_URL}/events/list`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }),
          axios.get(`${API_URL}/item-category/list/all`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }),
          axios.get(`${API_URL}/unit/list/all`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }),
          axios.get(`${API_URL}/tax-code/list/all`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }),
        ]);

      const vendorData = vendorsRes.data?.data || vendorsRes.data || [];
      setVendors(
        vendorData.filter((v) => v.is_active === true || v.is_active === 1),
      );

      const eventData = eventsRes.data?.data || eventsRes.data || [];
      setEvents(eventData);

      const categoryData = categoriesRes.data?.data || categoriesRes.data || [];
      setCategories(
        categoryData.filter((c) => c.is_active === true || c.is_active === 1),
      );

      const unitData = unitsRes.data?.data || unitsRes.data || [];
      setUnits(
        unitData.filter((u) => u.is_active === true || u.is_active === 1),
      );

      const taxCodeData = taxCodesRes.data?.data || taxCodesRes.data || [];
      setTaxCodes(
        taxCodeData.filter((t) => t.is_active === true || t.is_active === 1),
      );

      setDropdownsLoaded(true);
    } catch (err) {
      console.error("Failed to load dropdown data:", err);
      toastError("Failed to load form data");
    } finally {
      setLoadingDropdowns(false);
    }
  }, [API_URL, token, dropdownsLoaded]);

  useEffect(() => {
    fetchPOs();
  }, [fetchPOs]);

  // ─── Form Handlers ──────────────────────────────────────

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAdd = async () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormError("");
    setShowForm(true);
    setFormLoading(true);

    try {
      // Fetch dropdown data
      await fetchDropdownData();

      // Fetch approval document for PO
      const approvals = await fetchApprovalDocument();

      setForm({
        ...EMPTY_FORM,
        po_date: new Date().toISOString().split("T")[0],
        po_number: "",
        approvals: approvals,
      });
    } catch (err) {
      // Fallback with empty approvals
      setForm({
        ...EMPTY_FORM,
        po_date: new Date().toISOString().split("T")[0],
        po_number: "",
        approvals: [],
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openEdit = async (po) => {
    await fetchDropdownData();

    setFormError("");
    setIsEditMode(true);
    setEditingId(po.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const data = await fetchPOById(po.id);
      setForm({
        event_id: data.event_id || "",
        vendor_code: data.vendor_code || "",
        vendor_name: data.vendor_name || "",
        po_number: data.po_number || "",
        po_date: data.po_date || "",
        required_date: data.required_date || "",
        delivery_date: data.delivery_date || "",
        priority: data.priority || "Medium",
        currency: data.currency || "INR",
        currency_rate: data.currency_rate || 1,
        department: data.department || "",
        contact_person: data.contact_person || "",
        email: data.email || "",
        phone: data.phone || "",
        vendor_reference_no: data.vendor_reference_no || "",
        vendor_address: data.vendor_address || "",
        payment_term: data.payment_term || "Net 30",
        subtotal: data.subtotal || 0,
        subtotal_fc: data.subtotal_fc || 0,
        vat_sum: data.vat_sum || 0,
        vat_sum_fc: data.vat_sum_fc || 0,
        discount: data.discount || 0,
        discount_fc: data.discount_fc || 0,
        doc_total: data.doc_total || 0,
        doc_total_fc: data.doc_total_fc || 0,
        status: data.status || "Draft",
        remarks: data.remarks || "",
        lines: data.lines || [],
        approvals: data.approvals || [],
      });
    } catch (err) {
      setFormError("Failed to load PO details.");
      toastError("Failed to load PO details.");
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setIsEditMode(false);
    setEditingId(null);
  };

  // ─── Line Item Handlers ─────────────────────────────────

  const addLine = () => {
    const newLine = {
      ...EMPTY_LINE,
      line_no: form.lines.length + 1,
    };
    setF("lines", [...form.lines, newLine]);
  };

  const updateLine = (index, field, value) => {
    const updatedLines = [...form.lines];
    const line = { ...updatedLines[index] };

    // Numeric fields that cannot be negative
    const nonNegativeFields = ["qty", "price", "discount_percent", "tax_rate"];

    if (
      nonNegativeFields.includes(field) &&
      value !== "" &&
      Number(value) < 0
    ) {
      return;
    }

    // Percentage fields maximum 100
    const percentageFields = ["discount_percent", "tax_rate"];

    if (
      percentageFields.includes(field) &&
      value !== "" &&
      Number(value) > 100
    ) {
      return;
    }

    line[field] = value;

    const qty = parseFloat(line.qty) || 0;
    const price = parseFloat(line.price) || 0;
    const discountPercent = parseFloat(line.discount_percent) || 0;
    const taxRate = parseFloat(line.tax_rate) || 0;

    line.line_total = qty * price;

    line.discount_amount = (line.line_total * discountPercent) / 100;

    line.line_total_fc = line.line_total;
    line.discount_amount_fc = line.discount_amount;

    const afterDiscount = line.line_total - line.discount_amount;

    line.tax_amount = (afterDiscount * taxRate) / 100;

    line.tax_amount_fc = line.tax_amount;

    line.total_amount = afterDiscount + line.tax_amount;

    line.total_amount_fc = line.total_amount;

    updatedLines[index] = line;

    setF("lines", updatedLines);
    calculateTotals(updatedLines);
  };

  const removeLine = (index) => {
    const updatedLines = form.lines.filter((_, i) => i !== index);
    updatedLines.forEach((line, i) => (line.line_no = i + 1));
    setF("lines", updatedLines);
    calculateTotals(updatedLines);
  };

  const calculateTotals = (lines) => {
    let subtotal = 0;
    let vatSum = 0;
    let discount = 0;
    let docTotal = 0;

    lines.forEach((line) => {
      const lineTotal = parseFloat(line.line_total) || 0;
      const discountAmount = parseFloat(line.discount_amount) || 0;
      const taxAmount = parseFloat(line.tax_amount) || 0;
      const totalAmount = parseFloat(line.total_amount) || 0;

      subtotal += lineTotal;
      discount += discountAmount;
      vatSum += taxAmount;
      docTotal += totalAmount;
    });

    setF("subtotal", subtotal);
    setF("subtotal_fc", subtotal);
    setF("discount", discount);
    setF("discount_fc", discount);
    setF("vat_sum", vatSum);
    setF("vat_sum_fc", vatSum);
    setF("doc_total", docTotal);
    setF("doc_total_fc", docTotal);
  };
  const isValidDate = (date) => {
    if (!date) return false;

    const parsedDate = new Date(`${date}T00:00:00`);
    return !Number.isNaN(parsedDate.getTime());
  };

  const getToday = () => {
    return new Date().toISOString().split("T")[0];
  };

  // ─── Submit Handlers ────────────────────────────────────

  const handleSubmit = async (status = "Draft") => {
    // Prevent duplicate submission
    if (submitLockRef.current || formLoading) {
      return;
    }

    setFormError("");

    // ─────────────────────────────────────────
    // HEADER VALIDATION
    // ─────────────────────────────────────────

    if (!form.vendor_code) {
      setFormError("Vendor is required.");
      toastWarning("Please select a vendor.");
      return;
    }

    // Check selected vendor is valid
    const selectedVendor = vendors.find(
      (v) => v.vendor_code === form.vendor_code,
    );

    if (!selectedVendor) {
      setFormError("Please select a valid vendor.");
      toastWarning("Please select a valid vendor.");
      return;
    }

    if (!form.po_date) {
      setFormError("PO date is required.");
      toastWarning("PO date is required.");
      return;
    }

    if (!isValidDate(form.po_date)) {
      setFormError("PO date is invalid.");
      toastWarning("Please select a valid PO date.");
      return;
    }

    if (!form.required_date) {
      setFormError("Required date is required.");
      toastWarning("Required date is required.");
      return;
    }

    if (!isValidDate(form.required_date)) {
      setFormError("Required date is invalid.");
      toastWarning("Please select a valid required date.");
      return;
    }

    // Required date cannot be before PO date
    if (form.required_date < form.po_date) {
      setFormError("Required date cannot be before PO date.");
      toastWarning("Required date cannot be before PO date.");
      return;
    }

    // Delivery date validation
    if (form.delivery_date) {
      if (!isValidDate(form.delivery_date)) {
        setFormError("Delivery date is invalid.");
        toastWarning("Please select a valid delivery date.");
        return;
      }

      if (form.delivery_date < form.po_date) {
        setFormError("Delivery date cannot be before PO date.");
        toastWarning("Delivery date cannot be before PO date.");
        return;
      }

      if (form.delivery_date < form.required_date) {
        setFormError("Delivery date cannot be before the required date.");
        toastWarning("Delivery date cannot be before the required date.");
        return;
      }
    }

    // Currency rate validation
    const currencyRate = Number(form.currency_rate);

    if (!Number.isFinite(currencyRate) || currencyRate <= 0) {
      setFormError("Currency rate must be greater than 0.");
      toastWarning("Currency rate must be greater than 0.");
      return;
    }

    // Event validation if selected
    if (form.event_id) {
      const selectedEvent = events.find(
        (event) => String(event.id) === String(form.event_id),
      );

      if (!selectedEvent) {
        setFormError("Please select a valid event.");
        toastWarning("Please select a valid event.");
        return;
      }
    }

    // ─────────────────────────────────────────
    // LINE VALIDATION
    // ─────────────────────────────────────────

    if (form.lines.length === 0) {
      setFormError("At least one line item is required.");
      toastWarning("At least one line item is required.");
      return;
    }

    for (let i = 0; i < form.lines.length; i++) {
      const line = form.lines[i];

      const qty = Number(line.qty);
      const price = Number(line.price);
      const discountPercent = Number(line.discount_percent);
      const taxRate = Number(line.tax_rate);

      // Category
      if (!line.item_category) {
        setFormError(`Line ${i + 1}: Category is required.`);
        toastWarning(`Line ${i + 1}: Please select a category.`);
        return;
      }

      const selectedCategory = categories.find(
        (c) => c.description === line.item_category,
      );

      if (!selectedCategory) {
        setFormError(`Line ${i + 1}: Invalid category.`);
        toastWarning(`Line ${i + 1}: Please select a valid category.`);
        return;
      }

      // Quantity
      if (!Number.isFinite(qty) || qty <= 0) {
        setFormError(`Line ${i + 1}: Quantity must be greater than 0.`);
        toastWarning(`Line ${i + 1}: Quantity must be greater than 0.`);
        return;
      }

      // Unit
      if (!line.unit) {
        setFormError(`Line ${i + 1}: Unit is required.`);
        toastWarning(`Line ${i + 1}: Please select a unit.`);
        return;
      }

      const selectedUnit = units.find((u) => u.description === line.unit);

      if (!selectedUnit) {
        setFormError(`Line ${i + 1}: Invalid unit.`);
        toastWarning(`Line ${i + 1}: Please select a valid unit.`);
        return;
      }

      // Price - no negative value
      if (!Number.isFinite(price) || price < 0) {
        setFormError(`Line ${i + 1}: Price cannot be negative.`);
        toastWarning(`Line ${i + 1}: Price cannot be negative.`);
        return;
      }

      // If price must be greater than 0, use this:
      if (price <= 0) {
        setFormError(`Line ${i + 1}: Price must be greater than 0.`);
        toastWarning(`Line ${i + 1}: Price must be greater than 0.`);
        return;
      }

      // Discount - no negative and max 100
      if (
        !Number.isFinite(discountPercent) ||
        discountPercent < 0 ||
        discountPercent > 100
      ) {
        setFormError(
          `Line ${i + 1}: Discount percentage must be between 0 and 100.`,
        );
        toastWarning(
          `Line ${i + 1}: Discount percentage must be between 0 and 100.`,
        );
        return;
      }

      // Tax cannot be negative
      if (!Number.isFinite(taxRate) || taxRate < 0 || taxRate > 100) {
        setFormError(`Line ${i + 1}: Tax rate must be between 0 and 100.`);
        toastWarning(`Line ${i + 1}: Tax rate must be between 0 and 100.`);
        return;
      }
    }

    // ─────────────────────────────────────────
    // LOCK SUBMISSION
    // ─────────────────────────────────────────

    submitLockRef.current = true;
    setFormLoading(true);

    // ─────────────────────────────────────────
    // CREATE PAYLOAD
    // ─────────────────────────────────────────

    const payload = {
      ...form,

      status,

      po_number: form.po_number || "",

      // IMPORTANT:
      // Send null instead of ""
      delivery_date: form.delivery_date || null,

      approvals: Array.isArray(form.approvals) ? form.approvals : [],
    };

    payload.event_id = payload.event_id ? parseInt(payload.event_id, 10) : 0;

    payload.currency_rate = parseFloat(payload.currency_rate) || 1;

    payload.subtotal = parseFloat(payload.subtotal) || 0;

    payload.subtotal_fc = parseFloat(payload.subtotal_fc) || 0;

    payload.vat_sum = parseFloat(payload.vat_sum) || 0;

    payload.vat_sum_fc = parseFloat(payload.vat_sum_fc) || 0;

    payload.discount = parseFloat(payload.discount) || 0;

    payload.discount_fc = parseFloat(payload.discount_fc) || 0;

    payload.doc_total = parseFloat(payload.doc_total) || 0;

    payload.doc_total_fc = parseFloat(payload.doc_total_fc) || 0;

    payload.lines = payload.lines.map((line) => ({
      ...line,

      qty: parseFloat(line.qty) || 0,

      consumed_qty: parseFloat(line.consumed_qty) || 0,

      price: parseFloat(line.price) || 0,

      price_fc: parseFloat(line.price) || 0,

      discount_percent: parseFloat(line.discount_percent) || 0,

      discount_amount: parseFloat(line.discount_amount) || 0,

      discount_amount_fc: parseFloat(line.discount_amount) || 0,

      tax_rate: parseFloat(line.tax_rate) || 0,

      line_total: parseFloat(line.line_total) || 0,

      line_total_fc: parseFloat(line.line_total) || 0,

      tax_amount: parseFloat(line.tax_amount) || 0,

      tax_amount_fc: parseFloat(line.tax_amount) || 0,

      total_amount: parseFloat(line.total_amount) || 0,

      total_amount_fc: parseFloat(line.total_amount) || 0,
    }));

    try {
      if (isEditMode) {
        await updatePO(editingId, payload);

        toastSuccess("Purchase Order updated successfully.");
      } else {
        const response = await createPO(payload);

        toastSuccess(
          `Purchase Order created: ${response.po_number || "Successfully"}`,
        );
      }

      closeForm();
      await fetchPOs();
    } catch (err) {
      const detail = err.response?.data?.detail;

      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save purchase order.";

      setFormError(msg);
      toastError(msg);
    } finally {
      submitLockRef.current = false;
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this purchase order?")
    ) {
      return;
    }

    try {
      await deletePO(id);
      toastSuccess("Purchase Order deleted successfully.");
      fetchPOs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toastError(detail || "Failed to delete purchase order.");
    }
  };

  // ── Filters ─────────────────────────────────────────────

  const filteredPOs = pos.filter((po) => {
    const matchStatus = statusFilter === "All" || po.status === statusFilter;
    const matchSearch =
      (po.po_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.vendor_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.vendor_code || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  // ── Format Helpers ──────────────────────────────────────

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    const map = {
      Draft: "po-status-draft",
      Submitted: "po-status-submitted",
      Approved: "po-status-approved",
      Rejected: "po-status-rejected",
      Cancelled: "po-status-cancelled",
    };
    return map[status] || "po-status-draft";
  };

  const getPriorityClass = (priority) => {
    const map = {
      Low: "priority-low",
      Medium: "priority-medium",
      High: "priority-high",
      Urgent: "priority-urgent",
    };
    return map[priority] || "priority-medium";
  };

  // ─── Form View ──────────────────────────────────────────

  if (showForm) {
    return (
      <div className="po-form-page">
        <div className="po-form-header">
          <div className="po-form-header-left">
            <button className="po-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="po-back-icon" />
            </button>
            <div>
              <h2 className="po-form-title">
                {isEditMode ? "Edit Purchase Order" : "New Purchase Order"}
              </h2>
              <p className="po-form-subtitle">
                {isEditMode
                  ? "Update PO details"
                  : "Create a new purchase order"}
              </p>
            </div>
          </div>
        </div>

        <div className="po-form-card">
          {loadingDropdowns && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Loading form data...
            </p>
          )}
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && <p className="po-form-error">{formError}</p>}

          <div className="po-form-section">
            <h3>Purchase Order Information</h3>
            <div className="po-form-grid">
              <div className="po-form-group">
                <label>PO Number</label>
                <input
                  value={
                    isEditMode ? form.po_number || "N/A" : "Auto-generated"
                  }
                  disabled
                  style={{
                    background: "#f5f5f5",
                    fontWeight: isEditMode ? "600" : "400",
                    color: isEditMode ? "#2A070C" : "#A27B5B",
                  }}
                />
                {!isEditMode && (
                  <div className="po-sequence-info">
                    <span className="po-sequence-label">
                      PO number will be auto-generated
                    </span>
                  </div>
                )}
              </div>
              <div className="po-form-group">
                <label>PO Date *</label>
                <input
                  name="po_date"
                  type="date"
                  value={form.po_date}
                  max={form.required_date || undefined}
                  onChange={(e) => setF("po_date", e.target.value)}
                />
              </div>
              <div className="po-form-group">
                <label>Required Date *</label>
                <input
                  name="required_date"
                  type="date"
                  value={form.required_date}
                  min={form.po_date || getToday()}
                  onChange={(e) => setF("required_date", e.target.value)}
                />
              </div>
              <div className="po-form-group">
                <label>Delivery Date</label>
                <input
                  name="delivery_date"
                  type="date"
                  value={form.delivery_date}
                  min={form.required_date || form.po_date || getToday()}
                  onChange={(e) => setF("delivery_date", e.target.value)}
                />
              </div>
              <div className="po-form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={form.priority}
                  onChange={(e) => setF("priority", e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="po-form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={(e) => setF("currency", e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="po-form-group">
                <label>Currency Rate</label>
                <input
                  name="currency_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.currency_rate}
                  onChange={(e) => setF("currency_rate", e.target.value)}
                />
              </div>
              <div className="po-form-group">
                <label>Department</label>

                <select
                  name="department"
                  value={form.department}
                  onChange={(e) => setF("department", e.target.value)}
                >
                  <option value="">Select Department</option>
                  <option value="Admin">Admin</option>
                  <option value="Operations">Operations</option>
                  <option value="Finance">Finance</option>
                </select>
              </div>
              <div className="po-form-group">
                <label>Payment Terms</label>
                <select
                  name="payment_term"
                  value={form.payment_term}
                  onChange={(e) => setF("payment_term", e.target.value)}
                >
                  {PAYMENT_TERMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="po-form-group">
                <label>Event</label>
                <select
                  name="event_id"
                  value={form.event_id}
                  onChange={(e) => setF("event_id", e.target.value)}
                  disabled={loadingDropdowns}
                >
                  <option value="">Select Event</option>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.event_title || e.title || `Event ${e.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="po-form-section">
            <h3>Vendor Information</h3>
            <div className="po-form-grid">
              <div className="po-form-group">
                <label>Vendor *</label>
                <select
                  name="vendor_code"
                  value={form.vendor_code}
                  onChange={(e) => {
                    const vendor = vendors.find(
                      (v) => v.vendor_code === e.target.value,
                    );
                    setF("vendor_code", e.target.value);
                    setF("vendor_name", vendor?.vendor_name || "");
                    setF("vendor_address", vendor?.address || "");
                    setF("contact_person", vendor?.contact_person || "");
                    setF("email", vendor?.email || "");
                    setF("phone", vendor?.mobile_no || "");
                  }}
                  disabled={loadingDropdowns}
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.vendor_code}>
                      {v.vendor_code} - {v.vendor_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="po-form-group">
                <label>Vendor Name</label>
                <input
                  value={form.vendor_name}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </div>
              <div className="po-form-group">
                <label>Contact Person</label>
                <input
                  name="contact_person"
                  value={form.contact_person}
                  onChange={(e) => setF("contact_person", e.target.value)}
                  placeholder="Contact person name"
                />
              </div>
              <div className="po-form-group">
                <label>Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setF("email", e.target.value)}
                  placeholder="vendor@example.com"
                />
              </div>
              <div className="po-form-group">
                <label>Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setF("phone", e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <div className="po-form-group">
                <label>Vendor Reference No</label>
                <input
                  name="vendor_reference_no"
                  value={form.vendor_reference_no}
                  onChange={(e) => setF("vendor_reference_no", e.target.value)}
                  placeholder="Vendor reference number"
                />
              </div>
              <div className="po-form-group po-col-full">
                <label>Vendor Address</label>
                <input
                  name="vendor_address"
                  value={form.vendor_address}
                  onChange={(e) => setF("vendor_address", e.target.value)}
                  placeholder="Vendor address"
                />
              </div>
            </div>
          </div>

          <div className="po-form-section">
            <div className="po-section-header">
              <h3>Line Items</h3>
              <button
                className="po-btn po-btn-primary po-btn-sm"
                onClick={addLine}
                disabled={loadingDropdowns}
              >
                + Add Line
              </button>
            </div>

            <div className="po-lines-wrap">
              <table className="po-lines-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Price</th>
                    <th>Discount %</th>
                    <th>Tax Code</th>
                    <th>Tax %</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="po-lines-empty">
                        No items added. Click "Add Line" to start.
                      </td>
                    </tr>
                  ) : (
                    form.lines.map((line, index) => (
                      <tr key={index}>
                        {/* Sr. No. */}
                        <td className="po-sr-no">{index + 1}</td>

                        {/* Category */}
                        <td>
                          <select
                            value={line.item_category}
                            onChange={(e) =>
                              updateLine(index, "item_category", e.target.value)
                            }
                            disabled={loadingDropdowns}
                          >
                            <option value="">Select</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.description}>
                                {c.description}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Description */}
                        <td>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) =>
                              updateLine(index, "description", e.target.value)
                            }
                            placeholder="Description"
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Quantity */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={line.qty}
                            onChange={(e) =>
                              updateLine(index, "qty", e.target.value)
                            }
                            style={{ width: "60px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Unit */}
                        <td>
                          <select
                            value={line.unit}
                            onChange={(e) =>
                              updateLine(index, "unit", e.target.value)
                            }
                            disabled={loadingDropdowns}
                          >
                            <option value="">Select</option>
                            {units.map((u) => (
                              <option key={u.id} value={u.description}>
                                {u.description}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Price */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.price}
                            onChange={(e) =>
                              updateLine(index, "price", e.target.value)
                            }
                            style={{ width: "80px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Discount */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={line.discount_percent}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (
                                value === "" ||
                                (Number(value) >= 0 && Number(value) <= 100)
                              ) {
                                updateLine(index, "discount_percent", value);
                              }
                            }}
                            style={{ width: "60px" }}
                            disabled={loadingDropdowns || formLoading}
                          />
                        </td>

                        {/* Tax Code */}
                        <td>
                          <select
                            value={line.tax_code}
                            onChange={(e) => {
                              const tax = taxCodes.find(
                                (t) => t.tax_code === e.target.value,
                              );

                              updateLine(index, "tax_code", e.target.value);

                              if (tax) {
                                updateLine(index, "tax_rate", tax.tax_rate);
                              }
                            }}
                            disabled={loadingDropdowns}
                          >
                            <option value="">Select</option>
                            {taxCodes.map((t) => (
                              <option key={t.id} value={t.tax_code}>
                                {t.tax_code}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Tax Rate */}
                        <td>{line.tax_rate}%</td>

                        {/* Total Amount */}
                        <td>{formatCurrency(line.total_amount)}</td>

                        {/* Action */}
                        <td>
                          <button
                            className="po-action-btn po-action-btn-delete"
                            onClick={() => removeLine(index)}
                            title="Remove"
                            disabled={loadingDropdowns}
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="po-action-icon"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="po-form-grid">
            <div className="po-form-group po-col-full">
              <label>Remarks</label>
              <textarea
                rows="3"
                value={form.remarks}
                onChange={(e) => setF("remarks", e.target.value)}
                placeholder="Additional remarks..."
                disabled={loadingDropdowns}
              />
            </div>
          </div>

          {form.lines.length > 0 && (
            <div className="po-totals">
              <div className="po-total-item">
                <span>Subtotal</span>
                <strong>{formatCurrency(form.subtotal)}</strong>
              </div>
              <div className="po-total-item">
                <span>Discount</span>
                <strong>{formatCurrency(form.discount)}</strong>
              </div>
              <div className="po-total-item">
                <span>Tax</span>
                <strong>{formatCurrency(form.vat_sum)}</strong>
              </div>
              <div className="po-total-item po-total-grand">
                <span>Grand Total</span>
                <strong>{formatCurrency(form.doc_total)}</strong>
              </div>
            </div>
          )}

          <div className="po-form-footer">
            <button className="po-btn po-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            {isEditMode && (
              <>
                <button
                  className="po-btn po-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="po-btn po-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Saving..." : "Submit"}
                </button>
              </>
            )}
            {!isEditMode && (
              <>
                <button
                  className="po-btn po-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="po-btn po-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Creating..." : "Submit"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────

  return (
    <div className="po-list-view">
      <div className="po-page-header">
        <div className="po-page-header-left">
          <h1 className="po-page-title">Purchase Orders</h1>
          <p className="po-page-subtitle">
            Manage purchase orders and approvals
          </p>
        </div>
        <div className="po-page-header-right">
          <div className="po-search-box">
            <span className="po-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search POs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="po-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button className="po-btn po-btn-primary" onClick={openAdd}>
            + Create PO
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="po-table-card">
        <div className="po-table-responsive">
          <table className="po-table">
            <thead>
              <tr>
                <th>#</th>
                <th>PO Number</th>
                <th>Vendor</th>
                <th>Date</th>
                <th>Priority</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="po-empty-row">
                    Loading...
                  </td>
                </tr>
              ) : filteredPOs.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="po-empty-state">
                      <div className="po-empty-icon">📋</div>
                      <div className="po-empty-title">
                        No Purchase Orders Found
                      </div>
                      <div className="po-empty-desc">
                        {searchTerm
                          ? `No results for "${searchTerm}"`
                          : "Create your first purchase order"}
                      </div>
                      {!searchTerm && (
                        <button
                          className="po-btn po-btn-primary"
                          onClick={openAdd}
                        >
                          + Create PO
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPOs.map((po, index) => (
                  <tr key={po.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="po-number-badge">{po.po_number}</span>
                    </td>
                    <td>
                      <div className="po-vendor-info">
                        <div className="po-vendor-code">{po.vendor_code}</div>
                        <div className="po-vendor-name">{po.vendor_name}</div>
                      </div>
                    </td>
                    <td>{formatDate(po.po_date)}</td>
                    <td>
                      <span
                        className={`po-priority-badge ${getPriorityClass(po.priority)}`}
                      >
                        {po.priority || "Medium"}
                      </span>
                    </td>
                    <td>{formatCurrency(po.doc_total)}</td>
                    <td>
                      <span
                        className={`po-status-badge ${getStatusClass(po.status)}`}
                      >
                        {po.status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <div className="po-action-group">
                        <button
                          className="po-action-btn po-action-btn-edit"
                          onClick={() => openEdit(po)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="po-action-icon"
                          />
                        </button>
                        {po.status === "Draft" && (
                          <button
                            className="po-action-btn po-action-btn-delete"
                            onClick={() => handleDelete(po.id)}
                            title="Delete"
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="po-action-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  ROOT — Two-view page with tab switcher
// ─────────────────────────────────────────────────────────
export default function PurchaseOrders() {
  const [view, setView] = useState("list");

  return (
    <div className="po-wrapper">
      <div className="po-tab-bar">
        <button
          className={`po-tab-btn ${view === "list" ? "po-tab-active" : ""}`}
          onClick={() => setView("list")}
        >
          📋 Purchase Orders
        </button>
        <button
          className={`po-tab-btn ${view === "approvals" ? "po-tab-active" : ""}`}
          onClick={() => setView("approvals")}
        >
          ✅ Approvals
        </button>
      </div>

      {view === "list" && <PurchaseOrdersView />}
      {view === "approvals" && <POApprovals />}
    </div>
  );
}
